import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Artifacts } from '@axtria/hqi-genai'
import '@axtria/hqi-genai/lib/hqi-genai.css'
import { useChat } from '../../hooks/useChat';
import { useArtifactsReset } from '../../hooks/useArtifactsReset';
import { DEEP_LINK_PARAMS } from '../../components/Notification/NotificationUtils';

const toId = (value: string | null) => Number(value) || null;

const baseURL = process.env.REACT_APP_HQI_BASE_URL
const middlewareURL = process.env.REACT_APP_HQI_MIDDLEWARE_URL
const UMUrl = process.env.REACT_API_URL

const ArtifactsComponent = () => {
  const {
    userInfo,
    getTeamName,
    activeKnowledgeId,
    activeApp,
    isThinkingModeEnabled,
    apiVersionName
  } = useChat();

  const { artifactsResetCount } = useArtifactsReset();
  // The module keeps "list vs. opened artifact" in its own state and exposes no
  // reset prop, so a key bump is the only way back to the list from outside it.
  const [instanceKey, setInstanceKey] = useState(0);
  const isArtifactOpenRef = useRef(false);

  const handleArtifactOpen = useCallback(() => {
    // Opening an artifact just shows its readout in place, within the Artifacts
    // screen — the left nav should stay on "artifacts".
    isArtifactOpenRef.current = true;
  }, []);

  // Deep link from a notification, e.g. /artifacts?sharedArtifactId=12&readoutId=34.
  const [searchParams, setSearchParams] = useSearchParams();
  const sharedArtifactId = toId(searchParams.get(DEEP_LINK_PARAMS.sharedArtifactId));
  const readoutId = toId(searchParams.get(DEEP_LINK_PARAMS.readoutId));
  const initialArtifact = useMemo(
    () => (sharedArtifactId || readoutId ? { sharedArtifactId, readoutId } : undefined),
    [sharedArtifactId, readoutId]
  );
  // The module reads initialArtifact once on mount, so a new target needs a remount.
  const deepLinkKey = searchParams.toString();

  const handleBackToList = useCallback(() => {
    isArtifactOpenRef.current = false;
    // Drop the deep link so a later remount (reset/refresh) shows the list, not the artifact.
    if (sharedArtifactId || readoutId) setSearchParams({}, { replace: true });
  }, [sharedArtifactId, readoutId, setSearchParams]);

  useEffect(() => {
    // Nothing to reset when the list is already showing — avoid a needless refetch.
    if (!isArtifactOpenRef.current) return;
    isArtifactOpenRef.current = false;
    setInstanceKey((key) => key + 1);
  }, [artifactsResetCount]);

  return (
    <Artifacts
      key={`${instanceKey}-${deepLinkKey}`}
      DOMAIN={baseURL}
      DOMAIN_MIDDLEWARE={middlewareURL}
      DOMAIN_UM={UMUrl}
      PROJECT_ID={userInfo?.preferredProject}
      TEAM={getTeamName()}
      USERNAME={`${userInfo?.first_name || ""}`}
      KNOWLEDGE_ID={activeKnowledgeId}
      APPS={activeApp ? [activeApp] : []}
      config={{ thinking: isThinkingModeEnabled, validatorAgent: true, UI_Enhancement: apiVersionName === 'V2' }}
      onArtifactOpen={handleArtifactOpen}
      onBackToList={handleBackToList}
      initialArtifact={initialArtifact}
    />
  )
};

export default ArtifactsComponent;
